import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  EmailMessageBody,
  MessageAttachments,
  emailGalleryAttachments,
  isRichEmailMessage,
  isSubjectOnlyEmailMessage,
} from "@/components/inbox/MessageContent";
import { MessageBubbleFooter } from "@/components/inbox/MessageBubbleFooter";
import { inboxMessagePropType } from "@/lib/propTypes/inbox";
import { messageDisplayTimestamp } from "@/lib/format";
import {
  threadMessageRowClass,
  THREAD_INBOUND_EMAIL_WIDTH_CLASS,
  THREAD_OUTBOUND_EMAIL_WIDTH_CLASS,
  THREAD_OUTBOUND_EMAIL_RICH_WIDTH_CLASS,
} from "@/lib/inboxHelpers";

function formatDispatchError(code) {
  if (!code) return null;
  if (code === "gmail_reply_target_missing") {
    return "Could not determine who to reply to in this Gmail thread.";
  }
  if (code === "live_outbound_disabled" || String(code).includes("LIVE_OUTBOUND")) {
    return "Live outbound is disabled on this server (SELLIX_LIVE_OUTBOUND). Ask an admin to enable it, then retry.";
  }
  return String(code).replace(/_/g, " ");
}

function getArticleBorderClass(isDraft, isFailed) {
  if (isDraft) return "border-indigo-200 border-dashed";
  if (isFailed) return "border-red-200";
  return "border-slate-200";
}

function getOutboundMetaColorClass(isFailed, isDraft, isSending) {
  if (isFailed) return "text-red-600";
  if (isDraft || isSending) return "text-indigo-700";
  return "text-slate-500";
}

/**
 * Gmail/email thread message — same white email canvas for inbound and outbound
 * so formatting matches Gmail (not the indigo chat bubble).
 */
export function InboundEmailMessage({
  msg,
  assistantName = "Assistant",
  canRetry = false,
  onRetry,
  retryBusy = false,
}) {
  const isInbound = msg.direction === "inbound";
  const isDraft = msg.status === "draft";
  const isSending = msg.status === "sending";
  const isFailed = msg.status === "failed";
  const isDelivered = msg.status === "delivered";
  const isSent = msg.status === "sent";
  const metadata = msg.metadata || {};
  const dispatchMeta = metadata.outbound_dispatch || {};
  const dispatchError = formatDispatchError(dispatchMeta.error || dispatchMeta.reason);
  const gallery = emailGalleryAttachments(metadata);
  const subjectOnly = isSubjectOnlyEmailMessage(msg.body_text, metadata);
  const isRich = !subjectOnly && isRichEmailMessage(msg.body_text, metadata);
  const displayTime = messageDisplayTimestamp(msg);
  const showOutboundMeta = !isInbound;

  let emailWidthClass = THREAD_INBOUND_EMAIL_WIDTH_CLASS;
  if (!isInbound) {
    emailWidthClass = isRich ? THREAD_OUTBOUND_EMAIL_RICH_WIDTH_CLASS : THREAD_OUTBOUND_EMAIL_WIDTH_CLASS;
  }

  return (
    <div
      className={threadMessageRowClass(isInbound)}
      data-testid={isInbound ? `inbound-email-${msg.id}` : `outbound-email-${msg.id}`}
    >
      <div
        className={cn(
          "min-w-0 flex flex-col gap-1",
          isInbound ? "items-start" : "items-end",
          emailWidthClass,
        )}
      >
        <article
          className={cn(
            "min-w-0 overflow-hidden rounded-lg border bg-white shadow-sm",
            isInbound ? "w-full" : "max-w-full",
            getArticleBorderClass(isDraft, isFailed),
          )}
        >
          <EmailMessageBody text={msg.body_text} metadata={metadata} />
          <MessageAttachments attachments={gallery} className="px-4 pb-3 pt-0" />
          {isFailed && dispatchError && (
            <div className="border-t border-red-100 px-4 py-2 text-[11px] text-red-700 [overflow-wrap:anywhere]">
              Delivery failed: {dispatchError}
            </div>
          )}
          {isFailed && canRetry && onRetry && (
            <div className="border-t border-red-100 px-4 py-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[11px] border-red-200 text-red-700 hover:bg-red-100"
                disabled={retryBusy}
                onClick={() => onRetry(msg.id)}
                data-testid="outbound-retry-btn"
              >
                {retryBusy ? "Retrying…" : "Retry send"}
              </Button>
            </div>
          )}
        </article>
        {showOutboundMeta && (
          <div
            className={cn(
              "flex items-center gap-1 text-[10px] flex-wrap px-0.5",
              getOutboundMetaColorClass(isFailed, isDraft, isSending),
            )}
          >
            <MessageBubbleFooter
              isInbound={false}
              isDraft={isDraft}
              isSending={isSending}
              isFailed={isFailed}
              isDelivered={isDelivered}
              isSent={isSent}
              assistantName={assistantName}
              status={msg.status}
              displayTime={displayTime}
            />
          </div>
        )}
      </div>
    </div>
  );
}

InboundEmailMessage.propTypes = {
  msg: inboxMessagePropType.isRequired,
  assistantName: PropTypes.string,
  canRetry: PropTypes.bool,
  onRetry: PropTypes.func,
  retryBusy: PropTypes.bool,
};
